<div class="container">

  <header class="page-heading">
    <h1>SIP Calculator</h1>
    <p>Calculate mutual fund SIP returns, maturity value and wealth gained with this online SIP (Systematic Investment Plan) calculator.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">SIP Details</div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('monthlyInvestment')">
        <label class="field-label" for="monthlyInvestment">Monthly Investment</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="monthlyInvestment" type="number"
            [(ngModel)]="monthlyInvestment"
            [min]="C.monthlyInvestment.min" [max]="C.monthlyInvestment.max" [step]="C.monthlyInvestment.step"
            (ngModelChange)="onFieldChange('monthlyInvestment')"
            (blur)="onFieldBlur('monthlyInvestment')"
            placeholder="e.g. 10,000"
            autocomplete="off"/>
        </div>
        <div class="field-hint">{{ C.monthlyInvestment.min | currency:'INR':'symbol':'1.0-0':'en-IN' }} – {{ C.monthlyInvestment.max | currency:'INR':'symbol':'1.0-0':'en-IN' }} per month</div>
        @if (hasError('monthlyInvestment')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('monthlyInvestment') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('annualRate')">
        <label class="field-label" for="annualRate">Expected Annual Return</label>
        <div class="input-shell suffix">
          <input id="annualRate" type="number"
            [(ngModel)]="annualRate"
            [min]="C.rate.min" [max]="C.rate.max" [step]="C.rate.step"
            (ngModelChange)="onFieldChange('annualRate')"
            (blur)="onFieldBlur('annualRate')"
            placeholder="e.g. 12"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('annualRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('annualRate') }}
          </div>
        }
      </div>
    </div>

    <div class="field-group" [class.has-error]="hasError('tenureYears')">
      <label class="field-label" for="tenureYears">Investment Duration</label>
      <div class="input-shell suffix">
        <input id="tenureYears" type="number"
          [(ngModel)]="tenureYears"
          [min]="C.tenureYears.min" [max]="C.tenureYears.max" [step]="C.tenureYears.step"
          (ngModelChange)="onFieldChange('tenureYears')"
          (blur)="onFieldBlur('tenureYears')"
          placeholder="e.g. 10"
          autocomplete="off"/>
        <span class="affix right">Yr</span>
      </div>
      @if (hasError('tenureYears')) {
        <div class="field-error">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ fieldError('tenureYears') }}
        </div>
      }
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">Maturity Value</div>
        <div class="sc-value">{{ fmt.currency(result.maturityValue) }}</div>
        <div class="sc-sub">after {{ tenureYears }} years</div>
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Total Invested</div>
        <div class="sc-value">{{ fmt.currency(result.totalInvested) }}</div>
        <div class="sc-sub">{{ fmt.currency(monthlyInvestment) }}/mo</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Wealth Gained</div>
        <div class="sc-value">{{ fmt.currency(result.totalGain) }}</div>
        <div class="sc-sub">@ {{ annualRate }}% p.a.</div>
      </div>
    </div>

    <div class="card charts-card mb-md">
      <div class="section-title">Visual Breakdown</div>
      <div class="charts-top-row">
        <div class="chart-panel">
          <div class="chart-panel-label">Invested vs Gain</div>
          <div class="chart-canvas-wrap chart-canvas-pie">
            <canvas #breakdownRef></canvas>
          </div>
        </div>
        <div class="chart-panel chart-panel-bar">
          <div class="chart-panel-label">Corpus Growth</div>
          <div class="chart-canvas-wrap">
            <canvas #growthRef></canvas>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Growth Schedule</div>

      <div class="tab-bar">
        <button type="button" class="tab-btn" [class.active]="activeTab==='yearly'" (click)="activeTab='yearly'">
          Yearly Summary
        </button>
        <button type="button" class="tab-btn" [class.active]="activeTab==='monthly'" (click)="activeTab='monthly'">
          Full Monthly Schedule
        </button>
        <div class="tab-count">
          @if (activeTab === 'yearly') { {{ result.years.length }} years }
          @else { {{ result.months.length }} months }
        </div>
      </div>

      @if (activeTab === 'yearly') {
        <div class="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th class="t-left">Year</th>
                <th>Total Invested</th>
                <th class="t-balance">Corpus Value</th>
              </tr>
            </thead>
            <tbody>
              @for (row of result.years; track row.year) {
                <tr>
                  <td class="t-left t-year">Year {{ row.year }}</td>
                  <td>{{ fmt.currency(row.invested) }}</td>
                  <td class="t-balance">{{ fmt.currency(row.balance) }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      @if (activeTab === 'monthly') {
        <div class="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th class="t-left">#</th>
                <th>Total Invested</th>
                <th class="t-balance">Corpus Value</th>
              </tr>
            </thead>
            <tbody>
              @for (row of result.months; track row.month) {
                <tr>
                  <td class="t-left t-num">Month {{ row.month }}</td>
                  <td>{{ fmt.currency(row.invested) }}</td>
                  <td class="t-balance">{{ fmt.currency(row.balance) }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How is SIP Return Calculated?</h2>
    <p>
      A Systematic Investment Plan (SIP) lets you invest a fixed amount every month into a mutual
      fund. This calculator compounds each month's investment at your expected monthly return,
      assuming the investment is made at the start of each month.
    </p>
    <p class="emi-formula">FV = P &times; [((1 + i)<sup>n</sup> &minus; 1) / i] &times; (1 + i)</p>
    <ul class="emi-formula-vars">
      <li><strong>P</strong> &mdash; Monthly investment amount</li>
      <li><strong>i</strong> &mdash; Monthly return rate (annual return &divide; 12 &divide; 100)</li>
      <li><strong>n</strong> &mdash; Number of months invested</li>
    </ul>
    <p>
      For example, investing &#8377;10,000 every month for 10 years at an expected 12% annual
      return grows to a corpus of about &#8377;23.2 lakh, of which &#8377;12 lakh is your own
      investment and the rest is market-linked growth.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>Are SIP returns guaranteed?</h3>
      <p>
        No. SIP returns depend on the market performance of the underlying mutual fund and are not
        guaranteed. The rate you enter is an assumption for planning purposes, not a promised return.
      </p>
    </div>
    <div class="faq-item">
      <h3>Why does SIP work better than a lumpsum in volatile markets?</h3>
      <p>
        SIPs use rupee-cost averaging — you buy more units when prices are low and fewer when prices
        are high, which can smooth out the impact of market volatility over the long term.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more:
      <a routerLink="/learn/sip-vs-lumpsum">SIP vs lump sum: what does the math actually say?</a>,
      <a routerLink="/learn/step-up-sip-explained">what is a step-up SIP?</a> or
      <a routerLink="/learn/realistic-sip-returns-india">what's a realistic SIP return to assume?</a>
    </p>
    <p class="learn-crosslink">
      Also see: <a routerLink="/step-up-sip">Step-Up SIP Calculator</a>,
      <a routerLink="/sip-vs-step-up-sip">SIP vs Step-Up SIP Calculator</a>,
      <a routerLink="/lumpsum">Lumpsum Calculator</a>,
      <a routerLink="/cagr">CAGR Calculator</a> and
      <a routerLink="/learn/cagr-vs-xirr">CAGR vs XIRR — which one should you use?</a>
    </p>
  </div>
</section>
